/**
 * Faza 1: zaawansowany („Excel vibe") widok ładunków BULK oparty o Tabulator.
 *
 * Tryb opcjonalny — przełączany obok klasycznej tabeli w teczce spedycyjnej
 * (resources/views/shipping-folders/partials/_bulk_cargo.blade.php).
 * Read-only: sortowanie, filtry per kolumna, pokaż/ukryj + przenoszenie kolumn,
 * eksport CSV. Szerokość/sort/filtr w localStorage; widoczność kolumn per-user
 * na serwerze (users.preferences, czarna lista ukrytych). Edycja inline w Fazie 2.
 *
 * Tabulator ładowany leniwie (dynamic import) → osobny chunk, nie obciąża
 * głównego bundla na stronach bez gridu.
 *
 * Dokumentacja: docs/03-modules/bulk-cargo-grid/README.md
 */

let bulkCargoTable = null;
let bulkCargoTableLoading = false;

// URL-e i flagi uprawnień (czytane z data-* na #bulk-cargo-grid-wrap przy montażu)
let gridConfig = {
    statusUrl: '',
    transportCreateUrl: '',
    duplicateBase: '',
    canManage: false,
    canDelete: false,
    canTransport: false,
};

const STORAGE_KEY = 'bulkCargoViewMode';
// v3: + minWidth kolumn (czytelne nagłówki). Bump unieważnia stary układ z
// localStorage (inaczej utrwalone wąskie szerokości chowałyby nagłówki).
const PERSISTENCE_ID = 'bulk-cargo-grid-v3';

const STATUS_OPTIONS = {
    planowany: 'Planowany',
    w_przygotowaniu: 'W przygotowaniu',
    w_transporcie: 'W transporcie',
    w_porcie: 'W porcie',
    w_odprawie: 'W odprawie',
    dostarczone: 'Dostarczone',
    opozniony: 'Opóźniony',
    problem: 'Problem',
    anulowane: 'Anulowane',
};

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

// Zbiór UKRYTYCH pól (pomijamy selektor bez field i kolumnę akcji 'id').
function collectHiddenColumns(table) {
    return table.getColumns()
        .filter((c) => c.getField() && c.getField() !== 'id' && !c.isVisible())
        .map((c) => c.getField());
}

function saveHiddenColumns(table, prefsUrl, gridKey) {
    if (!table || !prefsUrl || !gridKey) return;
    fetch(prefsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() },
        body: JSON.stringify({ grid: gridKey, hidden: collectHiddenColumns(table) }),
    }).catch(() => {});
}

function gridSwal() {
    return window.Swal;
}

function gridGetSelectedIds() {
    if (!bulkCargoTable) {
        return [];
    }
    return bulkCargoTable.getSelectedData().map((row) => row.id);
}

function numberFormatter(cell) {
    const value = cell.getValue();
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return value;
    }
    return num.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function statusFormatter(cell) {
    const data = cell.getRow().getData();
    // Obrona w glab: dzis oba pola pochodza ze STALYCH map w modelu (BulkShipment::
    // getStatusColorAttribute), wiec nie sa danymi uzytkownika. Escapowanie kosztuje linie,
    // a chroni przed dniem, w ktorym ktos doda status definiowany w bazie — kolor ladowalby
    // wprost w atrybucie `style`, czyli w miejscu na wyjscie z atrybutu.
    const label = escapeHtmlAwizacja(data.status_label || '—');
    const color = escapeHtmlAwizacja(data.status_color || '#6B7280');
    return `<span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:11px;font-weight:600;color:#fff;background:${color}">${label}</span>`;
}

function consigneeFormatter(cell) {
    const data = cell.getRow().getData();
    const first = data.consignee || '';
    if (!first) {
        return '<span style="color:#cbd5e1">—</span>';
    }
    const count = Number(data.consignee_count || 0);
    if (count > 1) {
        const all = (data.consignee_all || '').replace(/"/g, '&quot;');
        return `${first} <span title="${all}" style="margin-left:4px;padding:0 5px;border-radius:6px;background:#f1f5f9;color:#475569;font-size:10px">+${count - 1}</span>`;
    }
    return first;
}

// Pojedynczy przycisk akcji — jednolite, czytelne pole 26×26 z hoverem
function actionButton({ href, onclick, title, color, bg, icon }) {
    const base = `display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:6px;font-size:14px;color:${color};background:${bg};cursor:pointer;text-decoration:none`;
    if (href) {
        return `<a href="${href}" title="${title}" style="${base}"><i class="fas ${icon}"></i></a>`;
    }
    return `<span role="button" title="${title}" style="${base}" onclick="${onclick}"><i class="fas ${icon}"></i></span>`;
}

function actionsFormatter(cell) {
    const data = cell.getRow().getData();
    const parts = [];
    if (data.show_url) {
        parts.push(actionButton({ href: data.show_url, title: 'Podgląd', color: '#ea580c', bg: '#fff7ed', icon: 'fa-eye' }));
    }
    if (gridConfig.canManage && data.edit_url) {
        parts.push(actionButton({ href: data.edit_url, title: 'Edytuj', color: '#16a34a', bg: '#f0fdf4', icon: 'fa-edit' }));
    }
    if (gridConfig.canManage && data.duplicate_url) {
        parts.push(actionButton({ href: data.duplicate_url, title: 'Duplikuj ładunek', color: '#4f46e5', bg: '#eef2ff', icon: 'fa-copy' }));
    }
    if (gridConfig.canDelete && data.destroy_url) {
        const num = data.shipment_number || '';
        parts.push(actionButton({ onclick: `gridDeleteBulkShipment(event, ${Number(data.id)}, ${jsArg(num)})`, title: 'Usuń', color: '#dc2626', bg: '#fef2f2', icon: 'fa-trash-alt' }));
    }
    return `<div style="display:flex;gap:6px;align-items:center;justify-content:center;height:100%">${parts.join('')}</div>`;
}

// Menu nagłówka kolumny: pokaż/ukryj kolumny (excel-style). onToggle wywoływane po zmianie.
function makeColumnHeaderMenu(onToggle) {
    return function () {
        const menu = [];
        const columns = this.getColumns();
        for (const column of columns) {
            const definition = column.getDefinition();
            if (!definition.field || definition.field === 'id') {
                continue; // selektor / kolumna akcji — zawsze widoczna
            }
            const icon = document.createElement('i');
            icon.classList.add('fas');
            icon.classList.add(column.isVisible() ? 'fa-check-square' : 'fa-square');
            icon.style.marginRight = '6px';

            const label = document.createElement('span');
            label.textContent = definition.title;

            const item = document.createElement('span');
            item.appendChild(icon);
            item.appendChild(label);

            menu.push({
                label: item,
                action: function (e) {
                    e.stopPropagation();
                    column.toggle();
                    icon.classList.toggle('fa-check-square');
                    icon.classList.toggle('fa-square');
                    if (typeof onToggle === 'function') onToggle();
                },
            });
        }
        return menu;
    };
}

function buildColumns(hidden, headerMenu) {
    const num = { hozAlign: 'right', sorter: 'number', formatter: numberFormatter };
    const cols = [
        { formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', headerSort: false, headerFilter: false, frozen: true, width: 44, cssClass: 'tabulator-row-selector' },
        { title: 'Nr ładunku', field: 'shipment_number', frozen: true, minWidth: 140, headerMenu },
        { title: 'Status', field: 'status_label', formatter: statusFormatter, minWidth: 130, headerMenu },
        { title: 'Towar', field: 'goods_description', minWidth: 200, headerMenu },
        { title: 'Grupa', field: 'group_name', headerMenu },
        { title: 'Statek', field: 'vessel', headerMenu },
        { title: 'Armator', field: 'shipowner', headerMenu },
        { title: 'Agent', field: 'agent', headerMenu },
        { title: 'Odbiorca', field: 'consignee', formatter: consigneeFormatter, minWidth: 160, headerMenu },
        { title: 'Dostawca', field: 'supplier_name', headerMenu },
        { title: 'Shipper', field: 'shipper_name', headerMenu },
        { title: 'Kontrahent', field: 'contractor', headerMenu },
        { title: 'Typ kont.', field: 'container_type', headerMenu },
        { title: 'Nr kont.', field: 'container_number', headerMenu },
        { title: 'Ilość', field: 'quantity', ...num, headerMenu },
        { title: 'Tonaż (kg)', field: 'tonnage_kg', ...num, headerMenu },
        { title: 'Waga (kg)', field: 'weight', ...num, headerMenu },
        { title: 'Poz. (szt.)', field: 'cargo_pieces', ...num, headerMenu },
        { title: 'Cargo (kg)', field: 'cargo_weight', ...num, headerMenu },
        { title: 'Deklaracje', field: 'cargo_declarations', ...num, headerMenu },
        { title: 'POL', field: 'pol_port', headerMenu },
        { title: 'POD', field: 'pod_port', headerMenu },
        { title: 'Terminal', field: 'terminal', headerMenu },
        { title: 'ETD', field: 'etd', sorter: 'date', headerMenu },
        { title: 'ETA', field: 'eta', sorter: 'date', headerMenu },
        { title: 'Incoterms', field: 'incoterms', headerMenu },
        { title: 'Operacja', field: 'operation_type', headerMenu },
        { title: 'Transport', field: 'transport_type', headerMenu },
        { title: 'Stawka', field: 'rate', ...num, headerMenu },
        { title: 'Waluta', field: 'currency', headerMenu },
        { title: 'Free time', field: 'free_time_days', ...num, headerMenu },
        { title: 'Booking', field: 'booking_number', headerMenu },
        { title: 'MBL', field: 'mbl_number', headerMenu },
        { title: 'HBL', field: 'hbl_number', headerMenu },
        { title: 'Telex', field: 'telex_release_date', sorter: 'date', headerMenu },
        { title: 'FV sprzedaży', field: 'sales_invoice_number', headerMenu },
        { title: 'Wartość FV', field: 'sales_invoice_value', ...num, headerMenu },
        { title: 'Mies. zamk.', field: 'closing_month', headerMenu },
        { title: 'Akcje', field: 'id', formatter: actionsFormatter, headerSort: false, headerFilter: false, frozen: true, hozAlign: 'center', width: 150, minWidth: 150 },
    ];
    return cols.map((c) => (c.field && c.field !== 'id')
        ? { ...c, visible: !hidden.includes(c.field) }
        : c);
}


/**
 * Przerysowanie po wczytaniu danych.
 *
 * Z `maxHeight` (zamiast sztywnego `height`) tabela bierze wysokosc z TRESCI. Gdy Tabulator
 * zbuduje sie na kontenerze ukrytym — a zakladka BULK bywa ukryta w chwili wczytania strony —
 * tresc ma zero wysokosci i tak juz zostaje: dane sa wczytane, stronicowanie dziala,
 * a wierszy nie widac. Sztywna wysokosc to maskowala, bo rezerwowala miejsce z gory.
 */
function przerysujPoDanych(table, element) {
    if (!table) return;
    const odswiez = () => requestAnimationFrame(() => {
        try { table.redraw(true); } catch (e) { /* tabela zniszczona w miedzyczasie */ }
    });
    table.on('tableBuilt', odswiez);
    table.on('dataProcessed', odswiez);

    // Przerysowanie w chwili, gdy kontener STAJE SIE widoczny. Nie zakladamy niczego o tym,
    // jak strona przelacza zakladki (klasy, Alpine, pelne przeladowanie) — obserwator reaguje
    // na sam fakt pojawienia sie elementu w widoku. Bez tego tabela zbudowana w ukryciu
    // zostaje z zerowa wysokoscia: dane sa, stronicowanie dziala, wierszy nie widac.
    if (element && typeof IntersectionObserver === 'function') {
        const obserwator = new IntersectionObserver((wpisy) => {
            if (wpisy.some((w) => w.isIntersecting)) {
                odswiez();
            }
        });
        obserwator.observe(element);
    }
}

async function initBulkCargoGrid() {
    if (bulkCargoTable || bulkCargoTableLoading) {
        return bulkCargoTable;
    }
    const wrap = document.getElementById('bulk-cargo-grid-wrap');
    const el = document.getElementById('bulk-cargo-grid');
    if (!wrap || !el) {
        return null;
    }

    gridConfig = {
        statusUrl: wrap.dataset.statusUrl || '',
        transportCreateUrl: wrap.dataset.transportCreateUrl || '',
        duplicateBase: wrap.dataset.duplicateBase || '',
        canManage: wrap.dataset.canManage === '1',
        canDelete: wrap.dataset.canDelete === '1',
        canTransport: wrap.dataset.canTransport === '1',
        prefsUrl: wrap.dataset.prefsUrl || '',
        gridKey: wrap.dataset.gridKey || '',
        hiddenColumns: JSON.parse(wrap.dataset.hiddenColumns || '[]'),
    };

    bulkCargoTableLoading = true;
    try {
        const { TabulatorFull } = await import('tabulator-tables');
        await import('tabulator-tables/dist/css/tabulator_simple.min.css');

        bulkCargoTable = new TabulatorFull(el, {
            ajaxURL: wrap.dataset.gridUrl,
            layout: 'fitData',
            // Wysokosc dopasowana do tresci z gornym limitem. Sztywne `height` zostawialo
            // pol ekranu pustki przy jednym wierszu i spychalo kolejna tabele pod zagiecie —
            // user nie wiedzial, ze nizej cos jest.
            maxHeight: '70vh',
            placeholder: 'Brak ładunków BULK',
            movableColumns: true,
            selectableRows: true,
            // Utrwalamy tylko szerokość kolumn — frozen oraz kolejność zawsze z definicji
            // w kodzie (gwarantuje, że Akcje są frozen na prawo). Widoczność zarządzana
            // per-user przez serwer (data-hidden-columns), nie localStorage.
            persistence: { sort: true, filter: true, columns: ['width'] },
            persistenceID: PERSISTENCE_ID,
            columnDefaults: {
                resizable: true,
                headerFilter: 'input',
                // Minimalna szerokość — żeby nagłówek był czytelny nawet gdy kolumna
                // ma puste/krótkie dane (przy fitData zwężałaby się do kilku px).
                minWidth: 120,
            },
            columns: buildColumns(
                gridConfig.hiddenColumns,
                makeColumnHeaderMenu(debounce(
                    () => saveHiddenColumns(bulkCargoTable, gridConfig.prefsUrl, gridConfig.gridKey),
                    400
                )),
            ),
        });

        przerysujPoDanych(bulkCargoTable, el);

        bulkCargoTable.on('rowSelectionChanged', (data) => {
            const span = document.getElementById('bulk-cargo-grid-selected');
            if (span) {
                span.textContent = data.length > 0 ? `Zaznaczono: ${data.length}` : '';
            }
        });
    } catch (error) {
        console.error('Nie udało się załadować zaawansowanego widoku ładunków:', error);
        el.innerHTML = '<p style="padding:16px;color:#dc2626">Nie udało się załadować zaawansowanego widoku. Odśwież stronę lub wróć do trybu klasycznego.</p>';
    } finally {
        bulkCargoTableLoading = false;
    }
    return bulkCargoTable;
}

function updateToggleButtons(mode) {
    document.querySelectorAll('[data-bulk-view]').forEach((btn) => {
        const active = btn.dataset.bulkView === mode;
        btn.classList.toggle('bg-orange-600', active);
        btn.classList.toggle('text-white', active);
        btn.classList.toggle('bg-white', !active);
        btn.classList.toggle('text-gray-700', !active);
    });
}

// Runda naprawcza 1 (I-2 z recenzji): drugi parametr `persist` (domyślnie true — zachowuje
// dotychczasowe wywołania z przycisków przełącznika) — `false` pokazuje tryb TYLKO na ten
// render, bez zapisu do localStorage. Bez tego jeden klik „Awizuj zaznaczone" z Placu (patrz
// bootstrapBulkCargoView() niżej) TRWALE nadpisywał zapamiętaną preferencję widoku na
// WSZYSTKICH teczkach — parametr adresu nie może cicho zmieniać ustawienia użytkownika.
window.setBulkCargoView = async function (mode, persist = true) {
    const classic = document.getElementById('bulk-cargo-classic');
    const grid = document.getElementById('bulk-cargo-grid-wrap');
    if (!classic || !grid) {
        return;
    }

    if (mode === 'grid') {
        classic.classList.add('hidden');
        grid.classList.remove('hidden');
        const table = await initBulkCargoGrid();
        // Tabulator inicjowane na widocznym kontenerze; redraw jako bufor po unhide
        if (table) {
            setTimeout(() => table.redraw(true), 60);
        }
        const cargoTable = await initBulkCargoItemsGrid();
        if (cargoTable) {
            setTimeout(() => cargoTable.redraw(true), 80);
        }
        // Grid sztuk: Tabulator policzyl wysokosc na UKRYTYM kontenerze (zakladka BULK
        // jest schowana przy wczytaniu strony), wiec bez przerysowania tabela ma zero
        // wysokosci i wiersze sa niewidoczne — mimo poprawnie wczytanych danych.
        const piecesTable = await initBulkPiecesGrid();
        if (piecesTable) {
            setTimeout(() => piecesTable.redraw(true), 100);
        }
    } else {
        grid.classList.add('hidden');
        classic.classList.remove('hidden');
    }

    if (persist) {
        localStorage.setItem(STORAGE_KEY, mode);
    }
    updateToggleButtons(mode);
};

window.bulkCargoGridExportCsv = function () {
    if (!bulkCargoTable) {
        return;
    }
    bulkCargoTable.download('csv', 'ladunki-bulk.csv', { bom: true });
};

// ── Akcje zbiorcze (parytet 1:1 z paskiem akcji w trybie klasycznym) ──────────

function requireSelection() {
    const ids = gridGetSelectedIds();
    if (ids.length === 0) {
        const Swal = gridSwal();
        if (Swal) {
            Swal.fire({ title: 'Brak zaznaczenia', text: 'Zaznacz ładunki checkboxami.', icon: 'info' });
        } else {
            alert('Zaznacz ładunki');
        }
    }
    return ids;
}

window.gridBulkChangeStatus = function () {
    const ids = requireSelection();
    if (ids.length === 0) {
        return;
    }
    const Swal = gridSwal();
    Swal.fire({
        title: 'Zmień status ładunków',
        input: 'select',
        inputOptions: STATUS_OPTIONS,
        inputPlaceholder: 'Wybierz status',
        showCancelButton: true,
        confirmButtonText: 'Zmień',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#f97316',
        inputValidator: (value) => (!value ? 'Wybierz status' : undefined),
    }).then((result) => {
        if (!result.isConfirmed || !result.value) {
            return;
        }
        fetch(gridConfig.statusUrl, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({ ids, status: result.value }),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) {
                    throw new Error(data.message || 'Nie udało się zmienić statusu.');
                }
                return data;
            })
            .then((data) => {
                if (bulkCargoTable) {
                    bulkCargoTable.replaceData(); // odśwież z ajaxURL → nowe statusy
                }
                Swal.fire({ title: 'Gotowe', text: data.message, icon: 'success', timer: 1500, showConfirmButton: false });
            })
            .catch((error) => {
                Swal.fire({ title: 'Błąd', text: error.message, icon: 'error' });
            });
    });
};

window.gridBulkDuplicate = function () {
    const ids = requireSelection();
    if (ids.length === 0) {
        return;
    }
    const base = gridConfig.duplicateBase;
    if (ids.length === 1) {
        window.location.href = `${base}?duplicate=${ids[0]}`;
        return;
    }
    const Swal = gridSwal();
    Swal.fire({
        title: 'Duplikowanie ładunków BULK',
        html: `Czy na pewno chcesz zduplikować <strong>${ids.length} ładunków</strong>?<br><br>Każdy zostanie otwarty w nowej karcie do edycji przed zapisem.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, duplikuj',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#f97316',
    }).then((result) => {
        if (!result.isConfirmed) {
            return;
        }
        ids.forEach((id, index) => {
            setTimeout(() => window.open(`${base}?duplicate=${id}`, '_blank'), index * 200);
        });
        Swal.fire({ title: 'Otwarto karty', text: `Otwarto ${ids.length} kart z duplikatami.`, icon: 'success', timer: 2000, showConfirmButton: false });
    });
};

window.gridBulkTransportOrder = function () {
    const ids = requireSelection();
    if (ids.length === 0) {
        return;
    }
    window.location.href = `${gridConfig.transportCreateUrl}?bulk_ids=${ids.join(',')}`;
};

window.gridDeleteBulkShipment = function (event, id, number) {
    if (event) {
        event.stopPropagation();
    }
    if (!bulkCargoTable) {
        return;
    }
    const row = bulkCargoTable.getRow(id);
    const url = row?.getData()?.destroy_url;
    if (!url) {
        return;
    }
    const Swal = gridSwal();
    Swal.fire({
        title: 'Usunąć ładunek?',
        // Dotad pytanie nie mowilo ani slowa o tym, ze zabiera ze soba pozycje towarowe
        // i wszystkie sztuki. Serwer odmowi, jesli cokolwiek juz jedzie — ale user ma
        // wiedziec, co kasuje, ZANIM kliknie.
        text: (number ? `Ładunek ${number} zostanie usunięty` : 'Ładunek zostanie usunięty')
            + ' wraz ze WSZYSTKIMI pozycjami towarowymi i sztukami. Tego nie da się cofnąć.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#dc2626',
    }).then((result) => {
        if (!result.isConfirmed) {
            return;
        }
        fetch(url, {
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
            },
        })
            .then(async (response) => {
                // Serwer odmawia z POWODAMI (sztuki na skladzie, awizacja, zlecenie).
                // Bez odczytania tresci user widzialby samo „nie udalo sie" i nie wiedzial,
                // co odpiac.
                let dane = null;
                try { dane = await response.json(); } catch (e) { dane = null; }

                if (!response.ok || (dane && dane.success === false)) {
                    throw new Error((dane && dane.message) || 'Nie udało się usunąć ładunku.');
                }
                row.delete();
                Swal.fire({
                    title: 'Usunięto',
                    text: (dane && dane.message) || '',
                    icon: 'success',
                    timer: 2200,
                    showConfirmButton: false,
                });
            })
            .catch((error) => {
                Swal.fire({ title: 'Nie usunięto', text: error.message, icon: 'error' });
            });
    });
};

// ── Drugi grid: Jednostki ładunku (cargo items) — tracking wywozów ────────────

let cargoGridTable = null;
let cargoGridLoading = false;
// Stub nadpisywany kompletem przy montażu gridu (initBulkCargoItemsGrid).
let cargoGridConfig = { folderId: '', canManage: false, canTransport: false, bulkMarkUrl: '', bulkUnmarkUrl: '', prefsUrl: '', gridKey: '', hiddenColumns: [] };

function cargoStatusFormatter(cell) {
    const data = cell.getRow().getData();
    const color = data.status_color || '#6b7280';
    // Oś przydziału (pigułka) + osobny znacznik gotowości (Gotowe) — to dwie prostopadłe osie.
    const statusPill = `<span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:11px;font-weight:600;color:#fff;background:${color}">${data.status || ''}</span>`;
    const readyBadge = data.is_ready
        ? ` <span title="Oznaczone jako gotowe do wywozu" style="display:inline-block;padding:1px 7px;border-radius:9999px;font-size:11px;font-weight:600;color:#92400e;background:#fef3c7;border:1px solid #fde68a"><i class="fas fa-check"></i> Gotowe</span>`
        : '';
    return statusPill + readyBadge;
}

function cargoQtyFormatter(cell) {
    const value = cell.getValue();
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numVal = Number(value);
    if (Number.isNaN(numVal)) {
        return value;
    }
    const data = cell.getRow().getData();
    const formatted = numVal.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return `${formatted} <span style="color:#94a3b8">${data.unit_symbol || ''}</span>`;
}

function cargoVerticalFormatter(cell) {
    const map = { gora: 'Góra', srodek: 'Środek', dol: 'Dół' };
    return map[cell.getValue()] || '';
}

// Plac (Zadanie 7): etykieta placu (`plac_label`) + licznik dni (`plac_days`, gdy jest)
// doklejany tu, po stronie gridu. Wygaszone (szare/kursywa), gdy rzecz nie jest aktualnie
// na placu (`plac_state !== 'placed'`) — czyli w poczekalni, wywieziona lub wcale.
function cargoPlacFormatter(cell) {
    const data = cell.getRow().getData();
    const label = data.plac_label || 'nie na placu';
    const days = data.plac_days;
    const suffix = (days === null || days === undefined) ? '' : ` · ${days} ${days === 1 ? 'dzień' : 'dni'}`;
    const muted = data.plac_state !== 'placed';
    const style = muted ? 'color:#9ca3af;font-style:italic;font-size:12px' : 'color:#374151;font-size:12px';
    return `<span style="${style}"><i class="fas fa-layer-group" style="color:#d97706;margin-right:4px"></i>${escapeHtmlAwizacja(label)}${suffix}</span>`;
}

function cargoActionsFormatter(cell) {
    const data = cell.getRow().getData();
    const parts = [];
    if (cargoGridConfig.canManage && data.available > 0 && !data.is_ready) {
        const name = data.commodity_name || 'Cargo';
        const num = data.shipment_number || '';
        parts.push(`<button type="button" style="font-size:12px;padding:2px 8px;border:1px solid #93c5fd;border-radius:6px;color:#1d4ed8;background:#fff;cursor:pointer;white-space:nowrap" onclick="openMarkCargoReadyModal(${jsArg(data.id)},${jsArg(name)},${Number(data.available)},${jsArg(data.unit_symbol || 'szt')},${jsArg(num)})"><i class="fas fa-check"></i> Gotowe</button>`);
    }
    if (cargoGridConfig.canManage && data.is_ready) {
        parts.push(`<button type="button" style="font-size:12px;padding:2px 8px;border:1px solid #d1d5db;border-radius:6px;color:#374151;background:#fff;cursor:pointer;white-space:nowrap" onclick="gridCargoUnmark(event, ${Number(data.id)})"><i class="fas fa-times"></i> Anuluj</button>`);
    }
    // Kasowanie pozycji: do tej pory nie bylo zadnej drogi, wiec puste pozycje zostawione
    // przez przegrupowanie nie mialy czym zostac sprzatniete. Serwer odmowi, jesli pozycja
    // ma sztuki albo zywe zaangazowanie.
    if (cargoGridConfig.canManage) {
        const nazwa = data.commodity_name || data.position_reference || 'pozycja';
        parts.push(`<button type="button" title="Usuń pozycję towarową" style="font-size:12px;padding:2px 8px;border:1px solid #fecaca;border-radius:6px;color:#dc2626;background:#fff;cursor:pointer;white-space:nowrap" onclick="gridCargoDelete(event, ${Number(data.id)}, ${jsArg(nazwa)})"><i class="fas fa-trash-alt"></i></button>`);
    }
    return `<div style="display:flex;gap:6px;align-items:center;justify-content:flex-end">${parts.join('')}</div>`;
}

// Kolumna „Awizacja": lista przypisań cargo do zasobów (zasób[/wagon] · ilość szt) z usuwaniem,
// + przycisk „Awizuj" (otwiera modal w blade). Logika modala/usuwania żyje w _bulk_cargo.blade.php
// jako window.transportAwizujOpen / window.transportAwizujRemove (vanilla JS, prefiks transportAwizuj*).
function cargoAwizacjaFormatter(cell) {
    const data = cell.getRow().getData() || {};
    const items = Array.isArray(data.awizacje) ? data.awizacje : [];
    const availableForAwizacja = Number(data.available_for_awizacja ?? 0);
    const canManage = !!cargoGridConfig.canManage;

    const lines = items.map((a) => {
        const wagon = a.wagon_number ? ` / ${escapeHtmlAwizacja(a.wagon_number)}` : '';
        const label = escapeHtmlAwizacja(a.resource_label || '—');
        const dest = a.destination_name ? ` → ${escapeHtmlAwizacja(a.destination_name)}` : '';
        const date = formatAwizacjaDate(a.planned_date);
        const datePart = date ? ` · ${date}` : '';
        const qty = Number(a.quantity || 0).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        const remove = canManage
            ? `<span role="button" title="Usuń awizację" style="margin-left:6px;color:#dc2626;cursor:pointer" onclick="window.transportAwizujRemove && window.transportAwizujRemove(${Number(a.id)})"><i class="fas fa-times"></i></span>`
            : '';
        return `<div style="display:flex;align-items:center;gap:2px;font-size:12px;line-height:1.4"><span>${label}${wagon}${dest}${datePart} · ${qty} szt</span>${remove}</div>`;
    }).join('');

    // Nie-manager: tylko odczyt awizacji, bez przycisku „Awizuj".
    if (! canManage) {
        return `<div style="display:flex;flex-direction:column;gap:4px;align-items:flex-start;padding:2px 0">${lines || '<span style="color:#9ca3af;font-size:12px">—</span>'}</div>`;
    }

    const disabled = !(availableForAwizacja > 0);
    const btnStyle = disabled
        ? 'font-size:12px;padding:2px 8px;border:1px solid #e5e7eb;border-radius:6px;color:#9ca3af;background:#f9fafb;cursor:not-allowed;white-space:nowrap'
        : 'font-size:12px;padding:2px 8px;border:1px solid #fdba74;border-radius:6px;color:#c2410c;background:#fff;cursor:pointer;white-space:nowrap';
    const btnAttrs = disabled
        ? 'disabled'
        : `onclick="window.transportAwizujOpen && window.transportAwizujOpen(${Number(data.id)})"`;
    const btn = `<button type="button" style="${btnStyle}" ${btnAttrs} title="${disabled ? 'Brak dostępnej ilości do awizacji' : 'Awizuj ładunek na zasób'}"><i class="fas fa-truck-loading"></i> Awizuj</button>`;

    return `<div style="display:flex;flex-direction:column;gap:4px;align-items:flex-start;padding:2px 0">${lines}${btn}</div>`;
}

/**
 * Wartosc do wstawienia jako ARGUMENT funkcji w atrybucie `onclick`.
 *
 * Tekst przechodzi przez DWA parsery, wiec potrzebne sa dwie warstwy:
 *  1. `JSON.stringify` daje poprawny literal JavaScriptu (cudzyslowy, ukosniki, znaki
 *     sterujace) — samo wycinanie apostrofow tego nie zalatwia;
 *  2. `escapeHtmlAwizacja` zamienia `"` na `&quot;` oraz `<`/`>` na encje, bo literal
 *     siedzi w atrybucie HTML; przegladarka rozkoduje encje ZANIM odda tekst parserowi JS.
 *
 * Dlaczego to ma znaczenie: nazwy pozycji towarowych i numery ladunkow pochodza
 * z WGRANEGO PLIKU EXCEL, czyli ze zrodla niezaufanego. Pozycja nazwana
 * `<img src=x onerror=...>` wykonywalaby kod u kazdego, kto otworzy te teczke.
 */
function jsArg(value) {
    return escapeHtmlAwizacja(JSON.stringify(String(value ?? '')));
}

function escapeHtmlAwizacja(value) {
    return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Planowana data transportu (Y-m-d z backendu) → DD.MM.YYYY. Defensywnie: pusta przy braku/niepoprawnej.
function formatAwizacjaDate(value) {
    if (!value) {
        return '';
    }
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) {
        return '';
    }
    return `${m[3]}.${m[2]}.${m[1]}`;
}

function buildCargoColumns(hidden, headerMenu, config) {
    const qty = { hozAlign: 'right', sorter: 'number', formatter: cargoQtyFormatter };
    const cols = [];
    if (config.canTransport) {
        cols.push({ formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', headerSort: false, headerFilter: false, frozen: true, width: 44 });
    }
    cols.push(
        { title: 'Przesyłka', field: 'shipment_number', frozen: true, minWidth: 140 },
        { title: 'Nazwa cargo', field: 'commodity_name', minWidth: 240 },
        { title: 'Nr pozycji', field: 'position_reference', minWidth: 160 },
        { title: 'Ilość ogółem', field: 'quantity', ...qty },
        { title: 'W zleceniach', field: 'in_orders', ...qty },
        { title: 'Dostępne', field: 'available', ...qty },
        { title: 'Gotowe do wywozu', field: 'ready_quantity', ...qty },
        { title: 'Status', field: 'status', formatter: cargoStatusFormatter },
        { title: 'Hala/Plac', field: 'hala_plac' },
        { title: 'Plac', field: 'plac_label', formatter: cargoPlacFormatter, minWidth: 200 },
        { title: 'Pole odstawcze', field: 'pole_odstawcze' },
        { title: 'Rząd', field: 'rzad' },
        { title: 'Pozycja w pionie', field: 'pozycja_pionowa', formatter: cargoVerticalFormatter },
        { title: 'Miejsce', field: 'miejsce' },
        { title: 'Awizacja', field: 'awizacja', formatter: cargoAwizacjaFormatter, headerSort: false, headerFilter: false, minWidth: 200, width: 240 },
        { title: 'Akcje', field: 'id', formatter: cargoActionsFormatter, headerSort: false, headerFilter: false, frozen: true, hozAlign: 'right', width: 130 }
    );
    // headerMenu wstrzykiwany tu (kolumny cargo nie deklarują go w literałach) + widoczność z czarnej listy.
    return cols.map((c) => (c.field && c.field !== 'id')
        ? { ...c, headerMenu, visible: !hidden.includes(c.field) }
        : c);
}

async function initBulkCargoItemsGrid() {
    if (cargoGridTable || cargoGridLoading) {
        return cargoGridTable;
    }
    const wrap = document.getElementById('bulk-cargo-items-grid-wrap');
    const el = document.getElementById('bulk-cargo-items-grid');
    if (!wrap || !el) {
        return null;
    }

    cargoGridConfig = {
        folderId: wrap.dataset.folderId || '',
        canManage: wrap.dataset.canManage === '1',
        canTransport: wrap.dataset.canTransport === '1',
        bulkMarkUrl: wrap.dataset.bulkMarkUrl || '',
        bulkUnmarkUrl: wrap.dataset.bulkUnmarkUrl || '',
        yardQueueUrl: wrap.dataset.yardQueueUrl || '',
        prefsUrl: wrap.dataset.prefsUrl || '',
        gridKey: wrap.dataset.gridKey || '',
        hiddenColumns: JSON.parse(wrap.dataset.hiddenColumns || '[]'),
    };

    cargoGridLoading = true;
    try {
        const { TabulatorFull } = await import('tabulator-tables');
        await import('tabulator-tables/dist/css/tabulator_simple.min.css');

        cargoGridTable = new TabulatorFull(el, {
            ajaxURL: wrap.dataset.cargoGridUrl,
            layout: 'fitData',
            maxHeight: '60vh',
            placeholder: 'Brak jednostek ładunku',
            movableColumns: true,
            selectableRows: cargoGridConfig.canTransport,
            selectableRowsCheck: (row) => row.getData().selectable === true,
            persistence: { sort: true, filter: true, columns: ['width'] },
            persistenceID: 'bulk-cargo-items-grid-v1',
            columnDefaults: { resizable: true, headerFilter: 'input', minWidth: 120 },
            rowFormatter: cargoDayRowFormatter,
            columns: buildCargoColumns(
                cargoGridConfig.hiddenColumns,
                makeColumnHeaderMenu(debounce(
                    () => saveHiddenColumns(cargoGridTable, cargoGridConfig.prefsUrl, cargoGridConfig.gridKey),
                    400
                )),
                cargoGridConfig,
            ),
        });

        przerysujPoDanych(cargoGridTable, el);

        cargoGridTable.on('rowSelectionChanged', (data) => {
            const span = document.getElementById('bulk-cargo-items-selected');
            if (span) {
                span.textContent = data.length > 0 ? `Zaznaczono: ${data.length}` : '';
            }
        });

        // Plac (zadanie 3, centrum odwozów): wstępne zaznaczenie wierszy przekazanych z Placu
        // przez ?cargo_ids= (przycisk „Awizuj zaznaczone") — raz, po pierwszym załadowaniu
        // danych (dataLoaded odpala się też po replaceData(), stąd flaga). try/catch: brak
        // wiersza (np. w międzyczasie wywieziony) albo brak modułu zaznaczania (canTransport
        // wyłączony) nie może wywalić całego gridu.
        let cargoIdsFromYardApplied = false;
        cargoGridTable.on('dataLoaded', () => {
            if (cargoIdsFromYardApplied) {
                return;
            }
            cargoIdsFromYardApplied = true;
            try {
                const raw = new URLSearchParams(location.search).get('cargo_ids');
                const ids = (raw || '').split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0);
                if (ids.length === 0) {
                    return;
                }
                cargoGridTable.selectRow(ids);
                Promise.resolve(cargoGridTable.scrollToRow(ids[0], 'top', false)).catch(() => {});
            } catch (error) {
                console.error('Nie udało się wstępnie zaznaczyć wierszy z Placu:', error);
            }
        });
    } catch (error) {
        console.error('Nie udało się załadować gridu jednostek ładunku:', error);
        el.innerHTML = '<p style="padding:16px;color:#dc2626">Nie udało się załadować tabeli jednostek ładunku.</p>';
    } finally {
        cargoGridLoading = false;
    }
    return cargoGridTable;
}

window.bulkCargoItemsGridExportCsv = function () {
    if (cargoGridTable) {
        cargoGridTable.download('csv', 'jednostki-ladunku.csv', { bom: true });
    }
};

// Wspólny loader xlsx przez Vite (dynamic import → osobny lazy chunk).
// Eksportowany na window, bo inline-skrypty w bladach (np. _bulk_transport_resources)
// nie przechodzą przez Vite i nie mogą same rozwiązać bare-specifiera 'xlsx'.
window.__loadXlsx = async function () {
    const mod = await import('xlsx');
    // xlsx.mjs eksportuje named (utils/write/...), bez default — namespace = mod.
    return mod.default || mod;
};

window.bulkCargoItemsGridExportXlsx = async function () {
    if (!cargoGridTable) return;
    window.XLSX = await window.__loadXlsx(); // Tabulator szuka window.XLSX
    cargoGridTable.download('xlsx', 'jednostki-ladunku.xlsx', { sheetName: 'Jednostki' });
};

window.bulkCargoItemsGridExportPdf = async function () {
    if (!cargoGridTable) return;
    const jspdfMod = await import('jspdf');
    const jsPDF = jspdfMod.jsPDF || (jspdfMod.default && jspdfMod.default.jsPDF) || jspdfMod.default;
    // jspdf-autotable v3: jawnie aplikujemy plugin na klasę jsPDF, której używa Tabulator,
    // żeby istniała metoda prototypowa doc.autoTable() (Tabulator ją wywołuje przy download('pdf')).
    const autoTableMod = await import('jspdf-autotable');
    const applyPlugin = autoTableMod.applyPlugin || (autoTableMod.default && autoTableMod.default.applyPlugin);
    if (typeof applyPlugin === 'function') {
        applyPlugin(jsPDF);
    }
    window.jspdf = { jsPDF }; // Tabulator szuka window.jspdf.jsPDF
    cargoGridTable.download('pdf', 'jednostki-ladunku.pdf', {
        orientation: 'landscape',
        title: 'Jednostki ladunku - tracking wywozow',
    });
};

// Odświeżenie gridu jednostek ładunku (np. po imporcie pozycjonowania) bez przeładowania strony
window.reloadBulkCargoItemsGrid = function () {
    if (cargoGridTable) {
        cargoGridTable.replaceData();
        return true;
    }
    return false;
};

// Most dla awizacji (modal w _bulk_cargo.blade.php): zwraca dane wiersza cargo po id
// (commodity_name, available_for_awizacja, gross_per_unit, awizacje...). Null gdy brak.
window.bulkCargoItemsRowData = function (cargoId) {
    if (!cargoGridTable) {
        return null;
    }
    const row = cargoGridTable.getRow(cargoId);
    return row ? row.getData() : null;
};

// --- Filtr "Dzień obróbki": pokazuje jednostki niezaawizowane LUB zaawizowane na wybrany dzień ---
// Tryb domyślny (cargoDayOnlyAssigned=false): niezaawizowane + zaawizowane-na-dzień (te ostatnie podświetlone).
// Tryb "tylko na ten dzień" (cargoDayOnlyAssigned=true): wyłącznie jednostki zaawizowane na wybrany dzień.
let cargoDayFilterValue = '';
let cargoDayFilterApplied = false;
let cargoDayOnlyAssigned = false;

// Czy wiersz ma awizację na aktualnie wybrany dzień (używane przez filtr i rowFormatter).
function cargoDayRowMatches(data) {
    return (data.awizacje || []).some((a) => a && a.planned_date === cargoDayFilterValue);
}

function cargoDayFilterFn(data) {
    if (!cargoDayFilterValue) {
        return true;
    }
    const onDay = cargoDayRowMatches(data);
    if (cargoDayOnlyAssigned) {
        return onDay;
    }
    return onDay || (data.awizacje || []).length === 0; // niezaawizowane nadal widoczne
}

// Podświetlenie wierszy zaawizowanych na wybrany dzień (zielony tint przez klasę CSS w blade).
function cargoDayRowFormatter(row) {
    const el = row.getElement();
    if (!el) {
        return;
    }
    const match = !!cargoDayFilterValue && cargoDayRowMatches(row.getData() || {});
    el.classList.toggle('tabulator-row-day-match', match);
}

window.bulkCargoItemsSetDayFilter = function (day, onlyAssigned) {
    cargoDayFilterValue = day || '';
    cargoDayOnlyAssigned = !!onlyAssigned;
    if (!cargoGridTable) {
        return false;
    }
    // Zdejmij i ponownie nałóż filtr, żeby nowy tryb (onlyAssigned) zadziałał od razu.
    if (cargoDayFilterApplied) {
        cargoGridTable.removeFilter(cargoDayFilterFn);
        cargoDayFilterApplied = false;
    }
    if (cargoDayFilterValue) {
        cargoGridTable.addFilter(cargoDayFilterFn);
        cargoDayFilterApplied = true;
    }
    // Przerysuj, żeby rowFormatter przeliczył podświetlenie (np. przy zmianie dnia bez zmiany trybu).
    cargoGridTable.redraw(true);
    return true;
};

// Licznik jednostek zaawizowanych na wybrany dzień — po WSZYSTKICH danych, nie tylko przefiltrowanych.
window.bulkCargoItemsDayCount = function (day) {
    if (!cargoGridTable || !day) {
        return 0;
    }
    return cargoGridTable.getData().filter((d) => (d.awizacje || []).some((a) => a && a.planned_date === day)).length;
};

/**
 * Kasowanie POZYCJI towarowej z siatki.
 *
 * Serwer (BulkDeletionGuard) odmawia, gdy pozycja ma sztuki albo zywe zaangazowanie —
 * i podaje POWODY. Pokazujemy je wprost, bo samo „nie udalo sie" nie mowi, co odpiac.
 */
window.gridCargoDelete = function (event, id, nazwa) {
    if (event) {
        event.stopPropagation();
    }
    if (!cargoGridTable) {
        return;
    }
    const row = cargoGridTable.getRow(id);
    const url = row?.getData()?.destroy_url;
    if (!url) {
        return;
    }
    const Swal = gridSwal();
    Swal.fire({
        title: 'Usunąć pozycję towarową?',
        text: `Pozycja „${nazwa}" zniknie z przesyłki. Usunąć da się tylko pozycję bez sztuk `
            + 'i bez żywej awizacji — w przeciwnym razie zobaczysz powód odmowy.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń pozycję',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#dc2626',
    }).then((result) => {
        if (!result.isConfirmed) {
            return;
        }
        fetch(url, { method: 'DELETE', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() } })
            .then(async (response) => {
                let dane = null;
                try { dane = await response.json(); } catch (e) { dane = null; }

                if (!response.ok || (dane && dane.success === false)) {
                    throw new Error((dane && dane.message) || 'Nie udało się usunąć pozycji.');
                }
                cargoGridTable.replaceData();
                Swal.fire({ title: 'Usunięto', icon: 'success', timer: 1500, showConfirmButton: false });
            })
            .catch((error) => Swal.fire({ title: 'Nie usunięto', text: error.message, icon: 'error' }));
    });
};

window.gridCargoUnmark = function (event, id) {
    if (event) {
        event.stopPropagation();
    }
    if (!cargoGridTable) {
        return;
    }
    const url = cargoGridTable.getRow(id)?.getData()?.unmark_url;
    if (!url) {
        return;
    }
    const Swal = gridSwal();
    Swal.fire({
        title: 'Anulować oznaczenie?',
        text: "Usunie oznaczenie 'gotowe do wywozu' dla tej pozycji.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Anuluj oznaczenie',
        cancelButtonText: 'Wróć',
        confirmButtonColor: '#dc2626',
    }).then((result) => {
        if (!result.isConfirmed) {
            return;
        }
        fetch(url, { method: 'DELETE', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() } })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Nie udało się anulować oznaczenia.');
                }
                cargoGridTable.replaceData();
                Swal.fire({ title: 'Gotowe', icon: 'success', timer: 1200, showConfirmButton: false });
            })
            .catch((error) => Swal.fire({ title: 'Błąd', text: error.message, icon: 'error' }));
    });
};

// Awizacja zbiorcza (zaawansowany grid): zbieramy zaznaczone cargo i otwieramy modal awizacji na zasób
// (window.transportAwizujBulkOpen żyje w _bulk_cargo.blade.php). Bez redirectu do formularza zlecenia,
// bez ograniczenia „z jednej przesyłki" — awizacja zbiorcza może obejmować wiele przesyłek BULK.
window.gridCargoTransportOrder = function () {
    if (!cargoGridTable) {
        return;
    }
    const rows = cargoGridTable.getSelectedData();
    const Swal = gridSwal();
    if (rows.length === 0) {
        if (Swal) {
            Swal.fire({ title: 'Brak zaznaczenia', text: 'Zaznacz co najmniej jedno cargo.', icon: 'info' });
        } else {
            alert('Zaznacz co najmniej jedno cargo.');
        }
        return;
    }
    const cargoIds = rows.map((r) => r.id);
    if (typeof window.transportAwizujBulkOpen === 'function') {
        window.transportAwizujBulkOpen(cargoIds);
    }
};

// Wspólny POST { ids } dla zbiorczych akcji cargo (oznacz/anuluj) + odświeżenie gridu
function cargoBulkStatusRequest(url, ids, confirmTitle, confirmText, confirmBtn, confirmColor) {
    const Swal = gridSwal();
    Swal.fire({
        title: confirmTitle,
        text: confirmText,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: confirmBtn,
        cancelButtonText: 'Anuluj',
        confirmButtonColor: confirmColor,
    }).then((result) => {
        if (!result.isConfirmed) {
            return;
        }
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({ ids }),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) {
                    throw new Error(data.message || 'Operacja nie powiodła się.');
                }
                return data;
            })
            .then((data) => {
                if (cargoGridTable) {
                    cargoGridTable.deselectRow();
                    cargoGridTable.replaceData();
                }
                Swal.fire({ title: 'Gotowe', text: data.message, icon: 'success', timer: 1600, showConfirmButton: false });
            })
            .catch((error) => Swal.fire({ title: 'Błąd', text: error.message, icon: 'error' }));
    });
}

window.gridCargoBulkMarkReady = function () {
    if (!cargoGridTable) {
        return;
    }
    // Tylko pozycje z dostępną ilością i jeszcze nie oznaczone — reszta i tak pominięta po stronie serwera
    const ids = cargoGridTable.getSelectedData().filter((r) => r.available > 0 && !r.is_ready).map((r) => r.id);
    if (ids.length === 0) {
        gridSwal().fire({ title: 'Brak pozycji', text: 'Zaznacz pozycje z dostępną ilością, które nie są jeszcze oznaczone jako gotowe.', icon: 'info' });
        return;
    }
    cargoBulkStatusRequest(
        cargoGridConfig.bulkMarkUrl,
        ids,
        'Oznaczyć jako gotowe?',
        `${ids.length} poz. zostanie oznaczonych całą dostępną ilością.`,
        'Oznacz jako gotowe',
        '#ca8a04'
    );
};

window.gridCargoBulkUnmark = function () {
    if (!cargoGridTable) {
        return;
    }
    const ids = cargoGridTable.getSelectedData().filter((r) => r.is_ready).map((r) => r.id);
    if (ids.length === 0) {
        gridSwal().fire({ title: 'Brak pozycji', text: 'Zaznacz pozycje oznaczone jako gotowe, którym chcesz anulować oznaczenie.', icon: 'info' });
        return;
    }
    cargoBulkStatusRequest(
        cargoGridConfig.bulkUnmarkUrl,
        ids,
        'Anulować oznaczenie?',
        `${ids.length} poz. straci oznaczenie „gotowe do wywozu".`,
        'Anuluj oznaczenie',
        '#dc2626'
    );
};

/**
 * Plac (Zadanie 7): skierowanie zaznaczonych jednostek ładunku na plac.
 * Zwykły POST (bez fetch) — komunikat wraca flashem po przeładowaniu strony,
 * tak jak z tabeli kontenerów i packing listy.
 */
window.gridCargoQueueOnYard = function () {
    if (!cargoGridTable) {
        return;
    }
    const ids = cargoGridTable.getSelectedData().map((r) => r.id);
    if (ids.length === 0) {
        gridSwal().fire({ title: 'Brak zaznaczenia', text: 'Zaznacz co najmniej jedno cargo.', icon: 'info' });
        return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = cargoGridConfig.yardQueueUrl;

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = '_token';
    csrfInput.value = csrfToken();
    form.appendChild(csrfInput);

    ids.forEach((id) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'items[]';
        input.value = 'bulk:' + id;
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
};

function bootstrapBulkCargoView() {
    const toggle = document.getElementById('bulk-cargo-view-toggle');
    if (!toggle) {
        return; // strona bez sekcji ładunków BULK
    }
    // Plac (zadanie 3, centrum odwozów): link „Awizuj zaznaczone" doklada ?cargo_ids= — tylko
    // tryb zaawansowany umie podświetlić te wiersze (selectRow w initBulkCargoItemsGrid, wyżej),
    // więc wymuszamy go na TEN render niezależnie od zapamiętanej preferencji widoku (inaczej
    // komunikat „Zaznaczone pozycje są podświetlone" z YardController::orderBulk() byłby
    // nieprawdziwy). Runda naprawcza 1 (I-2 z recenzji): `persist=false` — to wymuszenie NIE
    // może cicho nadpisać zapamiętanej preferencji użytkownika (localStorage) na wszystkich
    // teczkach; bez tego jeden klik z Placu trwale przełączał domyślny widok.
    const fromYard = !!new URLSearchParams(location.search).get('cargo_ids');
    if (fromYard) {
        window.setBulkCargoView('grid', false);
        return;
    }
    const saved = localStorage.getItem(STORAGE_KEY) === 'grid' ? 'grid' : 'classic';
    if (saved === 'grid') {
        window.setBulkCargoView('grid');
    } else {
        updateToggleButtons('classic');
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapBulkCargoView);
} else {
    bootstrapBulkCargoView();
}

/* ════════════════════════════════════════════════════════════════════════════
   GRID SZTUK — ten sam komponent tabeli co „Pozycje towarowe", wiersz = jeden slab.

   Powstał, bo przy 962 slabach tabela pozycji pokazuje JEDEN wiersz („962 szt.
   gatunku X") i jest wtedy mało użyteczna: spedytor pracuje na pojedynczych
   numerach. Sortowanie, filtry w nagłówkach, ukrywanie kolumn i eksport mają
   na sztukach dokładnie taki sam sens — zmienia się tylko poziom danych.
   ════════════════════════════════════════════════════════════════════════════ */

let piecesGridTable = null;
let piecesGridLoading = false;

function wymiaryFormatter(cell) {
    const d = cell.getRow().getData();
    const czesci = [d.thickness, d.width, d.length].filter((v) => v !== null && v !== undefined && v !== '');
    return czesci.length ? czesci.map((v) => Number(v).toLocaleString('pl-PL')).join(' × ') : '—';
}

function wagaFormatter(cell) {
    const v = cell.getValue();
    if (v === null || v === undefined || v === '') return '—';
    const d = cell.getRow().getData();
    const etykieta = d.weight_measured ? 'rzeczywista' : 'deklarowana';
    return `${Number(v).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span class="text-xs text-gray-400">(${etykieta})</span>`;
}

function statusSztukiFormatter(cell) {
    const d = cell.getRow().getData();
    const kolory = {
        ready: 'bg-gray-100 text-gray-700',
        assigned: 'bg-blue-100 text-blue-800',
        shipped: 'bg-green-100 text-green-800',
    };
    const klasa = kolory[d.status] || 'bg-gray-100 text-gray-700';
    return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${klasa}">${d.status_label || d.status}</span>`;
}

function transportFormatter(cell) {
    const d = cell.getRow().getData();
    if (!d.resource_label) return '<span class="text-gray-400">—</span>';
    const wagon = d.wagon_number ? ` <span class="text-xs text-gray-500">· wagon ${d.wagon_number}</span>` : '';
    return `${d.resource_label}${wagon}`;
}

function buildPiecesColumns(hiddenColumns, headerMenu) {
    const ukryta = (field) => (hiddenColumns || []).includes(field);
    const kol = [
        { title: 'Nr sztuki', field: 'piece_number', frozen: true, minWidth: 160 },
        { title: 'Gatunek', field: 'grade', minWidth: 110 },
        { title: 'Wymiary (mm)', field: 'thickness', formatter: wymiaryFormatter, headerSort: false, minWidth: 180 },
        { title: 'Waga (kg)', field: 'weight', formatter: wagaFormatter, hozAlign: 'right', minWidth: 150 },
        { title: 'Rząd', field: 'position_row', hozAlign: 'right', minWidth: 90 },
        { title: 'Kolumna', field: 'position_column', minWidth: 100 },
        { title: 'Poziom', field: 'position_level', hozAlign: 'right', minWidth: 90 },
        { title: 'Miejsce', field: 'position_raw', minWidth: 110 },
        { title: 'Status', field: 'status_label', formatter: statusSztukiFormatter, minWidth: 130 },
        { title: 'Jedzie', field: 'resource_label', formatter: transportFormatter, minWidth: 180 },
        { title: 'Pozycja towarowa', field: 'cargo_name', minWidth: 180 },
        { title: 'Przesyłka', field: 'shipment_number', minWidth: 150 },
        { title: 'Nr zamówienia', field: 'order_number', minWidth: 150 },
        { title: 'Nr kontraktu', field: 'contract_number', minWidth: 150 },
        { title: 'Nr BL', field: 'bl_number', minWidth: 130 },
        { title: 'Uwaga', field: 'note', minWidth: 160 },
    ];

    return kol.map((c) => ({ ...c, visible: !ukryta(c.field), headerMenu }));
}

async function initBulkPiecesGrid() {
    if (piecesGridTable || piecesGridLoading) {
        return piecesGridTable;
    }
    const wrap = document.getElementById('bulk-pieces-grid-wrap');
    const el = document.getElementById('bulk-pieces-grid');
    if (!wrap || !el) {
        return null;
    }

    piecesGridLoading = true;
    const { TabulatorFull } = await import('tabulator-tables');

    const hiddenColumns = JSON.parse(wrap.dataset.hiddenColumns || '[]');
    const prefsUrl = wrap.dataset.prefsUrl || '';
    const gridKey = wrap.dataset.gridKey || 'bulk_pieces';

    piecesGridTable = new TabulatorFull(el, {
        ajaxURL: wrap.dataset.gridUrl,
        layout: 'fitData',
        maxHeight: '60vh',
        placeholder: 'Brak sztuk',
        movableColumns: true,
        pagination: true,
        paginationSize: 100,
        paginationSizeSelector: [50, 100, 250, 500],
        persistence: { sort: true, filter: true, columns: ['width'] },
        persistenceID: 'bulk-pieces-grid-v1',
        columnDefaults: { resizable: true, headerFilter: 'input', minWidth: 120 },
        columns: buildPiecesColumns(
            hiddenColumns,
            makeColumnHeaderMenu(debounce(
                () => saveHiddenColumns(piecesGridTable, prefsUrl, gridKey),
                400
            )),
        ),
    });

    przerysujPoDanych(piecesGridTable, el);

    piecesGridTable.on('tableBuilt', () => {
        const licznik = document.getElementById('bulk-pieces-grid-count');
        if (licznik) {
            licznik.textContent = piecesGridTable.getDataCount() + ' szt.';
        }
    });

    // Eksporty: Tabulator pobiera DOKŁADNIE to, co widać — z aktywnymi filtrami
    // i w ustawionej kolejności kolumn. To jest sens tej tabeli przy 962 slabach.
    window.bulkPiecesExport = function (format) {
        if (!piecesGridTable) return;
        if (format === 'xlsx') {
            piecesGridTable.download('xlsx', 'sztuki.xlsx', { sheetName: 'Sztuki' });
        } else {
            piecesGridTable.download('csv', 'sztuki.csv', { bom: true });
        }
    };

    piecesGridLoading = false;
    return piecesGridTable;
}

// Inicjalizacja idzie przez przelacznik widoku (tryb 'grid'), a nie przez DOMContentLoaded —
// zakladka BULK bywa przy wczytaniu ukryta, a Tabulator policzylby wtedy zerowa wysokosc.
